import DashboardCard from "../../../ui/DashboardCard/DashboardCard.tsx";
import type { AdultDashboardCompletedGigItemResponse, AdultDashboardReviewItemResponse } from "../../../../types/adultDashboardTypes.ts";
import { toReadableDate } from "../shared/dashboardDisplay.ts";

type AdultReviewsBoxProps = {
  reviews: AdultDashboardReviewItemResponse[];
  completedGigs: AdultDashboardCompletedGigItemResponse[];
  loading: boolean;
  className?: string;
};

const getFullName = (firstName?: string | null, lastName?: string | null, fallback = "Teen") => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  return fullName || fallback;
};

const getTeenName = (
  gig: AdultDashboardCompletedGigItemResponse,
  review?: AdultDashboardReviewItemResponse,
) => {
  return getFullName(
    gig.teenFirstName ?? review?.reviewerFirstName,
    gig.teenLastName ?? review?.reviewerLastName,
    "Teen",
  );
};

const getAssignmentContext = (gig: AdultDashboardCompletedGigItemResponse) => {
  const teensRequired = Math.max(1, gig.teensRequired || 1);
  const coveredSeats = Math.max(1, gig.coveredSeats || 1);
  if (teensRequired <= 1 && coveredSeats <= 1) {
    return null;
  }
  return `${coveredSeats} of ${teensRequired} teen seat${teensRequired === 1 ? "" : "s"}`;
};

const AdultReviewsBox = ({ reviews, completedGigs, loading, className }: AdultReviewsBoxProps) => {
  const previewCompletedGigs = completedGigs.slice(0, 3);
  const reviewsByRequestId = new Map(
    reviews
      .filter((review) => review.gigRequestId)
      .map((review) => [review.gigRequestId, review]),
  );

  return (
    <DashboardCard
      to="/adult/reviews"
      title="Completed Gigs"
      subtitle="Completed work with the latest reviews teens have shared about each gig."
      className={className}
    >
      {loading ? (
        <div className="rounded-lg border border-dashed border-myteengig-border bg-myteengig-background p-4">
          <p className="text-sm text-myteengig-text-muted">Loading reviews and completed gigs...</p>
        </div>
      ) : completedGigs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-myteengig-border bg-myteengig-background p-4">
          <p className="text-sm text-myteengig-text-muted">No completed gigs yet. Teen reviews will appear here once gigs close.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {previewCompletedGigs.map((gig) => {
            const review = reviewsByRequestId.get(gig.requestId);
            const teenName = getTeenName(gig, review);
            const assignmentContext = getAssignmentContext(gig);

            return (
              <div
                key={gig.requestId}
                className="rounded-lg border border-myteengig-border bg-gradient-to-br from-white via-myteengig-background to-emerald-50 p-3 shadow-sm"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-primary">
                      Completed with {teenName}
                    </p>
                    <p className="mt-1 font-semibold text-myteengig-text-primary">{gig.gigTitle ?? "Completed Gig"}</p>
                    {assignmentContext ? (
                      <p className="mt-1 text-xs font-medium text-myteengig-text-muted">Assignment: {assignmentContext}</p>
                    ) : null}
                  </div>
                  <span className="w-fit shrink-0 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-900">
                    Completed {toReadableDate(gig.completedAt)}
                  </span>
                </div>
                {review ? (
                  <div className="mt-3 border-l-4 border-amber-300 pl-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                        {review.rating.toFixed(1)} / 5
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                        Review from {teenName}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                      {review.message || `${teenName} left a rating for this completed gig.`}
                    </p>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-myteengig-text-muted">No teen review has been shared for this gig yet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
};

export default AdultReviewsBox;
